import { useState } from 'react'
import { Inbox } from 'lucide-react'
import { EmptyState, ErrorState, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useApprovalMutations, useApprovalTasks } from '@/features/attendance'
import { DecisionContextPanel } from '../components/DecisionContextPanel'
import { useTaskContext } from '../api'
import { ENTITY_KEY_LABELS } from '../types'

/**
 * One inbox for every approvable thing.
 *
 * <p>The list on the left, the selected task's own module context on the right — fetched in
 * one call, so the screen never renders a decision with the evidence still loading. That is
 * the failure the regularisation slice found and the reason the generic context endpoint
 * exists.
 */
export function ApprovalsPage() {
  useBreadcrumb()
  const tasks = useApprovalTasks()
  const [selected, setSelected] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  const context = useTaskContext(selected)
  const { approve, reject } = useApprovalMutations()

  async function decide(action: 'approve' | 'reject') {
    if (!selected) return
    setError(null)
    try {
      const mutation = action === 'approve' ? approve : reject
      await mutation.mutateAsync({ taskId: selected, comment: comment || undefined })
      setSelected(null)
      setComment('')
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That decision could not be recorded')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Approvals"
        subtitle="Everything waiting on you, with what each module can tell you about it."
      />

      <div className="grid gap-4 xl:grid-cols-12">
        <div className="xl:col-span-5">
          <Card>
            {tasks.isPending && <Skeleton className="m-4 h-40" />}
            {tasks.isError && <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />}
            {tasks.isSuccess && tasks.data.length === 0 && (
              <EmptyState
                title="Nothing waiting"
                description="Approvals assigned to you appear here, whatever kind of thing they are."
              />
            )}
            {(tasks.data?.length ?? 0) > 0 && (
              <ul className="divide-y divide-[var(--color-border)]">
                {tasks.data?.map((task) => (
                  <li key={task.taskId}>
                    <button
                      type="button"
                      aria-current={selected === task.taskId ? 'true' : undefined}
                      onClick={() => { setSelected(task.taskId); setComment(''); setError(null) }}
                      className={
                        'flex w-full flex-col items-start gap-1 p-3 text-start text-sm hover:bg-[var(--color-page)]'
                        + (selected === task.taskId ? ' bg-[var(--color-primary-soft)]' : '')
                      }
                    >
                      <span className="font-medium text-[var(--color-heading)]">{task.title}</span>
                      <span className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                        <StatusBadge
                          status={task.entityKey ?? 'unknown'}
                          tone="neutral"
                          label={ENTITY_KEY_LABELS[task.entityKey ?? ''] ?? task.entityKey ?? 'Approval'}
                        />
                        {task.stepName}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4 xl:col-span-7">
          {selected == null ? (
            <Card className="p-6">
              <EmptyState
                title="Pick something to decide"
                description="Its own module fills in whatever helps — a balance, the punches, a credential check."
              />
            </Card>
          ) : (
            <>
              <DecisionContextPanel context={context.data} loading={context.isPending} />

              <Card className="space-y-3 p-4">
                <label htmlFor="decision-comment" className="text-sm font-medium text-[var(--color-heading)]">
                  Comment
                </label>
                <textarea
                  id="decision-comment"
                  rows={2}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Optional on approval; worth writing on a rejection."
                  className="w-full rounded-[var(--radius-input)] border border-[var(--color-border)] bg-[var(--color-card)] p-2 text-sm"
                />
                {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    disabled={approve.isPending || reject.isPending}
                    onClick={() => void decide('approve')}
                  >
                    <Inbox className="size-4" aria-hidden="true" />
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={approve.isPending || reject.isPending}
                    onClick={() => void decide('reject')}
                  >
                    Reject
                  </Button>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
